import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import type { RootState } from '../store';
import { HYDRATE } from 'next-redux-wrapper';

import { CategoryItem } from '@remodeling/content';

// Define a type for the slice state
export interface GoodsCategoryState {
  categoryTree: CategoryItem[];
  categoryNow: CategoryItem | null;
  categoryPath: CategoryItem[];
}

// Define the initial state using that type
const initialState: GoodsCategoryState = {
  categoryTree: [],
  categoryNow: null,
  categoryPath: [],
};

export const goodsCategorySlice = createSlice({
  name: 'goodsCategory',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setCategoryTree: (state, action: PayloadAction<CategoryItem[]>) => {
      state.categoryTree = action.payload;
    },
    setCategoryNow: (
      state,
      action: PayloadAction<Pick<CategoryItem, 'categoryNo'>>,
    ) => {
      const givenCategoryNo = action.payload.categoryNo;

      let categoryNow = _.first(state.categoryTree);
      let categoryPath = categoryNow ? [categoryNow] : [];

      const stack: CategoryItem[] = [...state.categoryTree];
      let found: CategoryItem | null = null;

      do {
        const it = stack.pop();

        it?.children?.map((t) =>
          _.extend(t, { parent: _.omit(it, 'children') }),
        );

        if (it?.categoryNo === givenCategoryNo) {
          found = it;

          const itsPath = [found];
          let s = found;

          do {
            if (_.has(s, 'parent')) {
              s = _.get(s, 'parent');
              itsPath.push(s);
            } else {
              break;
            }
          } while (s);

          categoryPath = _.reverse(itsPath);
        } else {
          if (it?.children && it.children.length > 0) {
            it.children.forEach((t) => stack.push(t));
          }
        }
      } while (stack.length > 0);

      if (found && found.displayYn === 'Y') {
        categoryNow = _.omit(found, ['children']);
      }

      state.categoryNow = categoryNow || null;
      state.categoryPath = categoryPath || [];
    },
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      return {
        ...state,
        ...action.payload.goodsCategory,
      };
    },
  },
});

export const { setCategoryTree, setCategoryNow } = goodsCategorySlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectCategoryTree = (state: RootState) =>
  state.goodsCategory.categoryTree;
export const selectCategoryNow = (state: RootState) =>
  state.goodsCategory.categoryNow;
export const selectCategoryPath = (state: RootState) =>
  state.goodsCategory.categoryPath;

export default goodsCategorySlice;
